import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useLocation } from 'react-router-dom';
import { routePathMap } from 'modules/common';

export type NotesToastContext = {
  id?: string;
  showSuccess?: boolean;
  showError?: boolean;
  showMessage?: boolean;
  message: string;
  values?: Record<string, string>;
  messageElement?: React.ReactElement;
};

export type NotesToastContextType = {
  toastContext: NotesToastContext;
  setToastContext: (input: NotesToastContext) => void;
  toastContextList: NotesToastContext[];
  setToastContextList: React.Dispatch<
    React.SetStateAction<NotesToastContext[]>
  >;
  setToastContextListValue: (input: NotesToastContext) => void;
  dismiss: boolean;
};

const getDefaultToastContext = (): NotesToastContext => {
  return {
    id: 'default-id',
    showMessage: false,
    showSuccess: false,
    showError: false,
    message: 'toast.note.added'
  };
};

export const NotesToastContext = createContext<NotesToastContextType>({
  toastContext: getDefaultToastContext(),
  setToastContext: () => {},
  toastContextList: [],
  setToastContextList: () => {},
  setToastContextListValue: () => {},
  dismiss: false
});

export const useNotesToastContext = (): NotesToastContextType =>
  useContext(NotesToastContext);

const NotesToastContextProvider: React.FC = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const [toastContext, setToastContextState] = useState(
    getDefaultToastContext()
  );

  const [toastContextList, setToastContextList] = useState<NotesToastContext[]>(
    []
  );

  const [dismiss, setDimissState] = useState(false);

  const location = useLocation();
  useEffect(() => {
    setDimissState(location.pathname === routePathMap.smartDiary);
  }, [location.pathname]);

  const setToastContext = useCallback(
    ({
      showMessage = false,
      showSuccess = false,
      showError = false,
      message,
      values
    }: NotesToastContext) => {
      setToastContextState({
        id: 'default-id',
        showMessage,
        showSuccess,
        showError,
        message,
        values
      });
    },
    []
  );

  const setToastContextListValue = useCallback(
    ({
      id,
      showMessage = false,
      showSuccess = false,
      showError = false,
      message,
      values
    }: NotesToastContext) => {
      setToastContextList((prev: NotesToastContext[]) => {
        return [
          ...prev,
          {
            id,
            showMessage,
            showSuccess,
            showError,
            message,
            values
          }
        ];
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      toastContext,
      setToastContext,
      toastContextList,
      setToastContextList,
      setToastContextListValue,
      dismiss
    }),
    [
      toastContext,
      setToastContext,
      toastContextList,
      setToastContextList,
      setToastContextListValue,
      dismiss
    ]
  );

  return (
    <NotesToastContext.Provider value={value}>
      {children}
    </NotesToastContext.Provider>
  );
};

export default NotesToastContextProvider;
